import type { QueryKey } from '@tanstack/react-query';

import { useQuery } from '../../../ReactQuery';
import { get } from '../../../redux/api';
import type { ReportOption } from '../../../../shared/model-typings/reportoption';
import type { PaginatedResponseWithTotal } from '../../../../shared/types/routes/pagination';

type GetReportOptionsResponse = PaginatedResponseWithTotal<ReportOption>;

// ReportOptions aren't updated often, so we can cache them for a while.
const REPORT_OPTIONS_STALE_TIME = 1000 * 60 * 5; // 5 minutes

// Fetch the report options from the API
function fetchReportOptions() {
  return get<GetReportOptionsResponse>('/api/report-options');
}

export const getReportOptionsQueryKey = (organizationId: number): QueryKey => ['reportOptions', organizationId];

/**
 * Hook for fetching all saved report options for the authenticated user's organization.
 *
 * @param organizationId - The organization ID to fetch report options for.
 * @param shouldFetch - Will not fetch if false. Defaults to true.
 */
export default function useReportOptions(organizationId: number, shouldFetch: boolean = true) {
  return useQuery<GetReportOptionsResponse>({
    queryFn: fetchReportOptions,
    queryKey: getReportOptionsQueryKey(organizationId),
    enabled: shouldFetch,
    staleTime: REPORT_OPTIONS_STALE_TIME,
  });
}
